import React, { useEffect, useRef, useState } from "react";

/**
 * FloatingScrollbar — a single horizontal scrollbar pinned to the bottom of the
 * viewport that controls multiple scroll containers in sync. Visible whenever
 * ANY of the target containers has horizontal overflow.
 *
 * Props:
 *   - targetSelectors: array of CSS selectors for elements whose scrollLeft we mirror.
 */
export default function FloatingScrollbar({ targetSelectors }) {
    const proxyRef = useRef(null);
    const spacerRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [rect, setRect] = useState({ left: 0, width: 0 });

    useEffect(() => {
        const proxy = proxyRef.current;
        const spacer = spacerRef.current;
        if (!proxy || !spacer) return;

        const getTargets = () =>
            targetSelectors
                .map((sel) => document.querySelector(sel))
                .filter(Boolean);

        const update = () => {
            const targets = getTargets();
            if (!targets.length) {
                setVisible(false);
                return;
            }
            // Any target overflowing → show
            const overflowing = targets.filter((t) => t.scrollWidth > t.clientWidth);
            if (!overflowing.length) {
                setVisible(false);
                return;
            }
            // Anchor position/width to the first overflowing target in view
            const anchor = overflowing[0];
            const r = anchor.getBoundingClientRect();
            setVisible(true);
            setRect({ left: r.left, width: r.width });
            // Spacer width = max scrollWidth so any table's full range is reachable
            const maxScrollW = Math.max(...overflowing.map((t) => t.scrollWidth));
            spacer.style.width = maxScrollW + "px";
        };

        const onProxyScroll = () => {
            const targets = getTargets();
            for (const t of targets) {
                // Clamp per-target so a very short table doesn't get invalid scroll
                const max = t.scrollWidth - t.clientWidth;
                t.scrollLeft = Math.min(proxy.scrollLeft, max);
            }
        };

        update();
        proxy.addEventListener("scroll", onProxyScroll, { passive: true });
        window.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);

        // Observe each existing target for size changes
        const ro = new ResizeObserver(update);
        for (const t of getTargets()) {
            ro.observe(t);
        }

        // Watch the DOM for tables appearing/disappearing (archived/deleted sections toggling)
        const mo = new MutationObserver(() => {
            for (const t of getTargets()) {
                try { ro.observe(t); } catch { /* already observed */ }
            }
            update();
        });
        mo.observe(document.body, { childList: true, subtree: true });

        return () => {
            proxy.removeEventListener("scroll", onProxyScroll);
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
            ro.disconnect();
            mo.disconnect();
        };
    }, [targetSelectors]);

    return (
        <div
            ref={proxyRef}
            className="floating-scrollbar"
            style={{
                position: "fixed",
                bottom: 0,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                display: visible ? "block" : "none",
            }}
            aria-hidden="true"
            data-testid="floating-scrollbar"
        >
            <div ref={spacerRef} style={{ height: 1 }} />
        </div>
    );
}
